import React, { useMemo } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';
import { ColorLabel } from '../../../elements/general';
import moment from 'moment';
import { moneyNumberFormatter } from './helpers';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const DailySalesCharts = ({ data = null, currency = '' }) => {
  const { t } = useTranslation();

  const { quantityLabels, quantityData, moneyLabels, valuesData, costsData, profitsData } = useMemo(() => {
    const emptyArray = [];
    return {
      quantityLabels: data?.quantity?.labels || emptyArray,
      quantityData: data?.quantity?.data || emptyArray,
      moneyLabels: data?.money?.labels || data?.quantity?.labels || emptyArray,
      valuesData: data?.money?.values || emptyArray,
      costsData: data?.money?.costs || emptyArray,
      profitsData: data?.money?.profits || emptyArray
    };
  }, [data]);

  const formattedQuantityLabels = quantityLabels.map(lbl => {
    const m = moment(lbl, 'YYYY-MM-DD', true);
    return m.isValid() ? m.format('DD.MM') : lbl;
  });

  const quantityOptions = {
    maintainAspectRatio: false,
    responsive: true,
    layout: {
      padding: {
        top: 24, bottom: 24
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const value = ctx.raw ?? 0;
            return `${t('salesStatistics.Number of products sold')}: ${value}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: '#9D9D9D',
          font: { size: 14 },
          autoSkip: true,
          padding: 8
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (v) => Number.isInteger(v) ? v : '',
          color: '#9D9D9D',
          font: { size: 14 },
          padding: 8
        }
      }
    }
  };

  const quantityChartData = {
    labels: formattedQuantityLabels, datasets: [{
      label: t('salesStatistics.Number of products sold'),
      data: quantityData,
      backgroundColor: '#E8F7F4',
      borderColor: '#1AB394',
      borderWidth: 1,
      borderRadius: 6,
      barThickness: 10,
      borderSkipped: false
    }]
  };

  const moneyOptions = {
    maintainAspectRatio: false,
    responsive: true,
    layout: {
      padding: {
        top: 24, bottom: 24
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const value = ctx.raw ?? 0;
            const numberValue = Number(value);
            const formattedValue = Number.isFinite(numberValue) ?
              moneyNumberFormatter.format(numberValue) : String(value);

            return `${ctx.dataset.label}: ${formattedValue} ${currency}`;
          }
        }
      }
    }, scales: {
      x: {
        grid: { display: false }, ticks: {
          color: '#9D9D9D',
          font: { size: 14 },
          autoSkip: true,
          padding: 8
        }
      }, y: {
        beginAtZero: true,
        ticks: {
          callback: (v) => Number.isInteger(v) ? v : '',
          color: '#9D9D9D',
          font: { size: 14 },
          padding: 8
        }
      }
    }
  };

  const moneyChartData = {
    labels: formattedQuantityLabels,
    datasets: [
      {
        label: t('salesStatistics.Product cost'),
        data: valuesData,
        backgroundColor: '#EEECF8',
        borderColor: '#5842BE',
        borderWidth: 1,
        borderRadius: 6,
        barThickness: 10,
        borderSkipped: false
      },
      {
        label: t('salesStatistics.Cost of goods sold'),
        data: costsData,
        backgroundColor: '#FFF3F4',
        borderColor: '#DC3545',
        borderWidth: 1,
        borderRadius: 6,
        barThickness: 10,
        borderSkipped: false
      },
      {
        label: t('salesStatistics.Sales profit'),
        data: profitsData,
        backgroundColor: '#FEF7EE',
        borderColor: '#F8AC59',
        borderWidth: 1,
        borderRadius: 6,
        barThickness: 10,
        borderSkipped: false
      }
    ]
  };

  return (
    <>
      <Row className="mt-5">
        <Col>
          <Row className="d-flex flex-column justify-content-between">
            <Col>
              <h4 className="fs-3 text-dark mb-4">
                {t('salesStatistics.Number of products sold')}
              </h4>
            </Col>
          </Row>
          <Row>
            <Col className="d-flex align-items-center justify-content-center mb-3"
            >
              <ColorLabel color="#E8F7F4" borderColor="#1AB394" borderRadius="2px" width="18px" height="18px" />
              <span className="primary-color font-16">{t('salesStatistics.Number of products sold')}</span>
            </Col>
          </Row>

          <Row>
            <Col className="px-0" style={{ height: 430 }}>
              <Bar options={quantityOptions} data={quantityChartData} />
            </Col>
          </Row>
        </Col>
      </Row>

      <Row className="mt-5">
        <Col>
          <Row className="d-flex flex-column justify-content-between">
            <Col>
              <h4 className="fs-3 text-dark mb-4">
                {t('salesStatistics.Product sales revenue')}
              </h4>
            </Col>
          </Row>
          <Row className='justify-content-center gap-5'>
            <Col xs="auto" className="d-flex align-items-center justify-content-center mb-3 px-0"
            >
              <ColorLabel color="#EEECF8" borderColor="#5842BE" borderRadius="2px" width="18px" height="18px" />
              <span className="primary-color font-16">{t('salesStatistics.Product cost')}</span>
            </Col>
            <Col xs="auto"  className="d-flex align-items-center justify-content-center mb-3 px-0"
            >
              <ColorLabel color="#FFF3F4" borderColor="#DC3545" borderRadius="2px" width="18px" height="18px" />
              <span className="primary-color font-16">{t('salesStatistics.Cost of goods sold')}</span>
            </Col>
            <Col xs="auto" className="d-flex align-items-center justify-content-center mb-3 px-0"
            >
              <ColorLabel color="#FEF7EE" borderColor="#F8AC59" borderRadius="2px" width="18px" height="18px" />
              <span className="primary-color font-16">{t('salesStatistics.Sales profit')}</span>
            </Col>
          </Row>

          <Row>
            <Col className="px-0" style={{ height: 430 }}>
              <Bar options={moneyOptions} data={moneyChartData} />
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default DailySalesCharts;
